import environments from "@/environments";
import { ApiFileResult, useApi } from "@/hooks/useApi";
import { TimeStamp } from "@/models/timestamp";

export type PlotPlanMapping = {
  plot_plan_mapping_id: number;
  overview_plot_plan_id: number;
  detail_plot_plan_id: number;
  clip_x: number;
  clip_y: number;
  clip_w: number;
  clip_h: number;
  scale_x: number;
  scale_y: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  timestamp?: TimeStamp;
};

export type PlotPlanMappingsResponse = {
  plot_plan_mappings: PlotPlanMapping[];
};

export const usePlotPlanMappingApi = () => {
  const api = useApi();

  /**
   * 全体詳細プロットプラン紐付けの一覧を取得する
   */
  const fetchPlotPlanMappings = async (params: {
    overview_plot_plan_id?: number;
    detail_plot_plan_id?: number;
  }): Promise<PlotPlanMappingsResponse | undefined> => {
    const query = new URLSearchParams();
    if (params.overview_plot_plan_id !== undefined) {
      query.append("overview_plot_plan_id", String(params.overview_plot_plan_id));
    }
    if (params.detail_plot_plan_id !== undefined) {
      query.append("detail_plot_plan_id", String(params.detail_plot_plan_id));
    }
    const queryString = query.toString();
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings${queryString ? `?${queryString}` : ""}`;
    return api.get(endpoint);
  };

  /**
   * 全体詳細プロットプラン紐付けの取得を行う
   */
  const fetchPlotPlanMapping = async (plotPlanMappingId: number): Promise<PlotPlanMapping | undefined> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/${plotPlanMappingId}`;
    return api.get(endpoint);
  };

  /**
   * 全体詳細プロットプラン紐付けの登録を行う
   */
  const createPlotPlanMapping = async (params: {
    overview_plot_plan_id: number;
    detail_plot_plan_id: number;
    clip_x: number;
    clip_y: number;
    clip_w: number;
    clip_h: number;
    uploadfile: File | Blob;
    scale_x: number;
    scale_y: number;
    x: number;
    y: number;
    rotation: number;
    color: string;
  }): Promise<PlotPlanMapping | undefined> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/create`;
    const formData = new FormData();
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value instanceof Blob ? value : String(value));
    });
    return api.post(endpoint, formData);
  };

  /**
   * 全体詳細プロットプラン紐付けの更新を行う
   */
  const updatePlotPlanMapping = async (params: {
    plotPlanMappingId: number;
    payload: {
      clip_x: number;
      clip_y: number;
      clip_w: number;
      clip_h: number;
      uploadfile?: File | Blob;
      scale_x: number;
      scale_y: number;
      x: number;
      y: number;
      rotation: number;
      color: string;
      timestamp: {
        update_date: string;
      };
    };
    reloadCallback?: (error: any) => void;
  }): Promise<PlotPlanMapping | undefined> => {
    const { plotPlanMappingId, payload, reloadCallback } = params;
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/update/${plotPlanMappingId}`;
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === "timestamp") {
        formData.append("update_date", payload.timestamp.update_date);
      } else if (value !== undefined) {
        formData.append(key, value instanceof Blob ? value : String(value));
      }
    });
    return api.post(endpoint, formData, { reloadCallback });
  };

  /**
   * 全体詳細プロットプラン紐付けの削除を行う
   */
  const deletePlotPlanMapping = async (plotPlanMappingId: number): Promise<{ message: string } | undefined> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/delete/${plotPlanMappingId}`;
    return api.post(endpoint);
  };

  /**
   * 全体詳細プロットプラン紐付けの座標（x, y）を更新する
   */
  const updatePlotPlanMappingCoord = async (params: {
    plotPlanMappingId: number;
    payload: {
      x: number;
      y: number;
      timestamp: Partial<TimeStamp>;
    };
    reloadCallback?: (error: any) => void;
  }): Promise<PlotPlanMapping | undefined> => {
    const { plotPlanMappingId, payload, reloadCallback } = params;
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/coord/update/${plotPlanMappingId}`;
    return api.post(endpoint, payload, { reloadCallback });
  };

  /**
   * 全体詳細プロットプラン紐付けの回転角度（rotation）を更新する
   */
  const updatePlotPlanMappingRotation = async (params: {
    plotPlanMappingId: number;
    payload: {
      rotation: number;
      timestamp: {
        update_date: string;
      };
    };
    reloadCallback?: (error: any) => void;
  }): Promise<PlotPlanMapping | undefined> => {
    const { plotPlanMappingId, payload, reloadCallback } = params;
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/rotation/update/${plotPlanMappingId}`;
    return api.post(endpoint, payload, { reloadCallback });
  };

  /**
   * 全体詳細プロットプラン紐付けの表示スケール（scale_x, scale_y）と座標（x, y）を更新する
   *
   * リサイズ時にサイズだけでなく座標も一緒に更新する必要がある。
   * 例えば左方向や上方向にリサイズした場合、表示矩形の左上座標（x, y）が変化するため、
   * scale のみを更新すると x, y が据え置かれて矩形がずれてしまう。
   */
  const updatePlotPlanMappingScale = async (params: {
    plotPlanMappingId: number;
    payload: {
      scale_x: number;
      scale_y: number;
      timestamp: {
        update_date: string;
      };
    };
    reloadCallback?: (error: any) => void;
  }): Promise<PlotPlanMapping | undefined> => {
    const { plotPlanMappingId, payload, reloadCallback } = params;
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/scale/update/${plotPlanMappingId}`;
    return api.post(endpoint, payload, { reloadCallback });
  };

  /**
   * 全体詳細プロットプラン紐付けのクリップ画像ファイルの取得
   */
  const fetchPlotPlanMappingClipFile = async (plotPlanMappingId: number): Promise<Blob | undefined> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plan_mappings/clip/files/${plotPlanMappingId}`;
    const result = await api.get<Blob | ApiFileResult>(endpoint);
    if (result && "data" in result && result.data instanceof Blob) {
      return result.data;
    }
    return result as Blob | undefined;
  };

  return {
    fetchPlotPlanMappings,
    fetchPlotPlanMapping,
    createPlotPlanMapping,
    updatePlotPlanMapping,
    deletePlotPlanMapping,
    updatePlotPlanMappingCoord,
    updatePlotPlanMappingRotation,
    updatePlotPlanMappingScale,
    fetchPlotPlanMappingClipFile,
  } as const;
};
